import { useEffect, useState } from 'react';
import { Button, Copy, Empty, ErrorNote, Field as FormField, Input, Panel, Select, relativeTime } from './ui';

/**
 * Outbound actions: the Miniserver calls LoxLink when something happens at home.
 *
 * This is the only direction that works without a route into the house, because the
 * Miniserver initiates the connection. A Virtual Output pointed at the URL below turns any
 * event into an email or an HTTP call.
 */

interface Action {
  id: string;
  slug: string;
  name: string;
  kind: 'webhook' | 'email' | 'log';
  target: string;
  method: string;
  body_template: string;
  subject_template: string;
  token: string;
  enabled: boolean;
  max_per_hour: number;
  fires: number;
  last_fired_at: string | null;
  last_ok: boolean | null;
  last_error: string | null;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: init?.body ? { 'content-type': 'application/json' } : {},
  });
  const data = await res.json();
  if (!res.ok) throw new Error((data as { error?: string }).error ?? 'request failed');
  return data as T;
}

export function ActionsPanel({ canEdit, publicUrl }: { canEdit: boolean; publicUrl: string | null }) {
  const [actions, setActions] = useState<Action[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [testResult, setTestResult] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    slug: '',
    name: '',
    kind: 'webhook',
    target: '',
    method: 'POST',
    body_template: '{"event":"{{name}}","value":"{{value}}","at":"{{time}}"}',
    subject_template: '',
  });

  const load = (): void => {
    void call<{ actions: Action[] }>('/api/actions')
      .then((d) => {
        setActions(d.actions);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  };

  useEffect(load, []);

  async function create(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    try {
      await call('/api/actions', { method: 'POST', body: JSON.stringify(form) });
      setCreating(false);
      setForm({ ...form, slug: '', name: '', target: '' });
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function fire(action: Action): Promise<void> {
    try {
      const result = await call<{ ok: boolean; detail?: string; error?: string }>(
        `/api/actions/${action.id}/test`,
        { method: 'POST', body: '{}' }
      );
      setTestResult({ ...testResult, [action.id]: result.ok ? `✓ ${result.detail}` : `✗ ${result.error}` });
      load();
    } catch (err) {
      setTestResult({ ...testResult, [action.id]: `✗ ${err instanceof Error ? err.message : String(err)}` });
    }
  }

  const base = publicUrl ?? '';

  return (
    <div className="space-y-4">
      <Panel
        title="Outbound actions"
        actions={canEdit && <Button variant="primary" onClick={() => setCreating(!creating)}>{creating ? 'Cancel' : 'New action'}</Button>}
      >
        <p className="text-sm text-ink-300 mb-3">
          A Loxone Virtual Output calls LoxLink when something happens at home, and LoxLink
          forwards it on. This works without any route into the house, because the Miniserver
          makes the call itself.
        </p>

        {error && <ErrorNote>{error}</ErrorNote>}

        {creating && (
          <form onSubmit={create} className="space-y-3 mb-4 pb-4 border-b border-ink-800">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Name">
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                      slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
                    })
                  }
                  placeholder="Alarm triggered"
                  required
                />
              </FormField>
              <FormField label="Slug">
                <Input mono value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
              </FormField>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Kind">
                <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  <option value="webhook">webhook</option>
                  <option value="email">email</option>
                  <option value="log">log only</option>
                </Select>
              </FormField>
              <div className="col-span-2">
                <FormField label={form.kind === 'email' ? 'Email address' : form.kind === 'log' ? 'Not used' : 'Target URL'}>
                  <Input
                    mono
                    value={form.target}
                    onChange={(e) => setForm({ ...form, target: e.target.value })}
                    placeholder={form.kind === 'email' ? 'you@example.com' : 'https://example.com/hook'}
                    disabled={form.kind === 'log'}
                  />
                </FormField>
              </div>
            </div>
            <FormField
              label="Body template"
              hint="{{value}}, {{name}}, {{time}} and {{query.x}} are substituted — never evaluated"
            >
              <textarea
                value={form.body_template}
                onChange={(e) => setForm({ ...form, body_template: e.target.value })}
                rows={2}
                className="mono w-full bg-ink-900 border border-ink-800 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:border-accent/70"
              />
            </FormField>
            <Button type="submit" variant="primary">
              Create action
            </Button>
          </form>
        )}

        {actions === null ? (
          <Empty>Loading…</Empty>
        ) : actions.length === 0 ? (
          <Empty>No actions yet.</Empty>
        ) : (
          <div className="space-y-3">
            {actions.map((a) => (
              <div key={a.id} className="border border-ink-800 rounded-md p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                      a.last_ok === false ? 'bg-bad' : a.last_ok ? 'bg-good' : 'bg-ink-700'
                    }`}
                  />
                  <span className="text-sm text-ink-100">{a.name}</span>
                  <span className="mono text-xs text-ink-500">{a.kind}</span>
                  <span className="text-xs text-ink-500 flex-1 truncate">{a.target}</span>
                  <span className="text-xs text-ink-500">
                    {a.fires} fire{a.fires === 1 ? '' : 's'}
                    {a.last_fired_at && ` · ${relativeTime(a.last_fired_at)}`}
                  </span>
                </div>

                {a.last_error && <div className="text-xs text-bad">{a.last_error}</div>}
                {testResult[a.id] && (
                  <div className={`text-xs ${testResult[a.id]!.startsWith('✓') ? 'text-good' : 'text-bad'}`}>
                    {testResult[a.id]}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <span className="text-xs text-ink-500 w-24 shrink-0">Loxone calls</span>
                  <code className="mono text-xs text-ink-300 bg-ink-950 border border-ink-800 rounded px-2 py-1 truncate flex-1">
                    {base}/do/{a.slug}/{a.token}
                  </code>
                  <Copy text={`${base}/do/${a.slug}/${a.token}`} />
                </div>

                {canEdit && (
                  <div className="flex gap-2">
                    <Button onClick={() => void fire(a)}>Test now</Button>
                    <a href={`/api/actions/${a.id}/template.xml`} download>
                      <Button>Virtual Output XML</Button>
                    </a>
                    <Button
                      variant="danger"
                      onClick={() => {
                        if (!confirm(`Delete action "${a.name}"?`)) return;
                        void call(`/api/actions/${a.id}`, { method: 'DELETE' }).then(load);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
